import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Button, LinearProgress, Table, TableBody, TableContainer, Typography } from '@mui/material'
import { CompanyEventRow } from './CompanyEventRow'
import SourceApi from '../../../api/SourceApi'
import { normalizedName } from '../../../components/entities/CompanyName'
import ConvertiblesApi, { ConvertibleInput } from '../../../api/ConvertiblesApi'
import { NewConvertibleQuickForm } from './NewConvertibleQuickForm'
import { ShareEntity } from 'types/ShareEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { CompanyEntity } from 'types/CompanyEntity'
import { PRInputEntity } from 'types/PRInput'
import { SourceType } from 'types/SourceType'
import { isBefore } from 'date-fns'

type CompanyConvertiblesProps = {
    company: CompanyEntity
    selectedTargets: any[]
    associatedPRs: any
    setAssociatedPRs: () => void
    convertibles: ConvertibleEntity[] | null
    setConvertibles: (convertibles: ConvertibleEntity[]) => void
    sourceType: SourceType | null
    setSourceType: (sourceType: SourceType | null) => void
    selectedPR: PRInputEntity | null
    onArchivePR: (pr: PRInputEntity) => Promise<void>
}

async function addPRToConvertible(convertible: ConvertibleEntity, pr: PRInputEntity, sourceType: SourceType) {
    const sourceApi = new SourceApi()
    if (!convertible || !pr || !sourceType) {
        console.error(
            `addPRToConvertible: Missing ${convertible ? '' : 'convertible'} ${pr ? '' : 'pr'} ${sourceType ? '' : 'sourceType'}`,
        )
        return null
    }
    /*
    id char(100) not null,
    issue_id char(100) comment 'ID of issue (optional)',
    warrant_id char(100) comment 'Warrant (optional)',
    convertible_od char(100) comment 'Convertible (optional)',
    source_url varchar(1024) comment 'URL of source',
    source_type varchar(50) comment 'Type of source',
    title varchar(250) comment 'Title of source document',
    published_date datetime comment 'Published date and time of source document',
    internal_comment text comment 'Internal comment',
    release_datetime datetime comment 'Release date and time',
    */
    const newSource = {
        convertibleId: convertible.id,
        sourceUrl: pr.url,
        sourceType: sourceType,
        title: pr.title,
        publishedDate: new Date(pr.publishDate),
        releaseDatetime: new Date(pr.publishDate),
    }
    const createdSource = await sourceApi.create(newSource)

    return createdSource
}

export const CompanyConvertibles = ({
    company,
    selectedTargets,
    associatedPRs,
    setAssociatedPRs,
    convertibles,
    setConvertibles,
    sourceType,
    setSourceType,
    selectedPR,
    onArchivePR,
}: CompanyConvertiblesProps) => {
    const [newConvertibleFormOpen, setNewConvertibleFormOpen] = useState(false)

    const convertibleApi = new ConvertiblesApi()

    const handleCreateConvertible = async (share: ShareEntity, formData: Omit<ConvertibleInput, 'shareId'>) => {
        if (!company || !sourceType || !selectedPR) return
        const convertibleData = { ...formData, shareId: share.id }
        const result = await convertibleApi.create(convertibleData)

        if (result) {
            const sourceResult = await addPRToConvertible(result, selectedPR, sourceType)
            result.shareName = share.name
            if (convertibles) {
                setConvertibles([result, ...convertibles])
            } else {
                setConvertibles([result])
            }

            if (sourceResult) {
                if (onArchivePR) await onArchivePR(selectedPR)
            }
        }
    }

    const enableQuickCreateButton = sourceType === 'Publication'

    if (!convertibles) return <LinearProgress />

    return (
        <>
            <Button
                variant='contained'
                color='primary'
                size='small'
                onClick={() => setNewConvertibleFormOpen(true)}
                disabled={!enableQuickCreateButton}
            >
                Ny konvertibel för {normalizedName(company.name)}
            </Button>
            <NewConvertibleQuickForm
                company={company}
                open={newConvertibleFormOpen}
                setOpen={setNewConvertibleFormOpen}
                handleCreateConvertible={handleCreateConvertible}
            />
            {convertibles.length === 0 ? <Typography variant='body1'>Inga konvertibler hittades</Typography> : null}

            {convertibles.length > 0 ? (
                <TableContainer>
                    <Table>
                        <TableBody>
                            {convertibles
                                .sort((a, b) => {
                                    if (!!a.completed && !!b.completed) {
                                        return isBefore(a.publicationDate, b.publicationDate)
                                    }
                                    return a.completed ? 1 : -1
                                })
                                .map((convertible) => {
                                    return (
                                        <CompanyEventRow
                                            key={convertible.id}
                                            event={convertible}
                                            eventType='convertible'
                                            selectedRows={selectedTargets}
                                            associatedPRs={associatedPRs}
                                            setAssociatedPRs={setAssociatedPRs}
                                            sourceType={sourceType}
                                            setSourceType={setSourceType}
                                            selectedPR={selectedPR}
                                            onCreateSource={addPRToConvertible}
                                            onArchivePR={onArchivePR}
                                        >
                                            <NavLink to={`/admin/convertibles/${convertible.id}`} target='_blank'>
                                                {convertible.convertibleName}
                                            </NavLink>
                                        </CompanyEventRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : null}
        </>
    )
}
